import { useState, useEffect, useMemo, useRef } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { fmt } from '../../utils/formatNumber';
import styles from './LedgerAccountSelect.module.css';

const GROUP_ORDER  = ['asset', 'liability', 'equity', 'income', 'expenditure'];
const GROUP_LABELS = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', income: 'Income', expenditure: 'Expenses' };

const fmtBal = (bal) => {
  if (bal === undefined || bal === null) return null;
  return { side: bal >= 0 ? 'Dr' : 'Cr', amount: fmt(Math.abs(bal)) };
};

/**
 * Group-wise ledger account dropdown with search.
 *
 * Props:
 *   value       — selected account id (string | number)
 *   onChange    — (id: string) => void
 *   accounts    — flat array of account objects (each with .group.nature, .name)
 *   balanceMap  — optional { [id]: number } for showing Dr/Cr balances
 *   placeholder — trigger button label when nothing selected
 *   filterFn    — optional (account) => boolean to restrict visible options
 */
export default function LedgerAccountSelect({
  value,
  onChange,
  accounts = [],
  balanceMap,
  placeholder = '— Select account —',
  filterFn,
}) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref       = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  useEffect(() => {
    if (open) { setTimeout(() => searchRef.current?.focus(), 0); }
    else       { setSearch(''); }
  }, [open]);

  const grouped = useMemo(() => {
    const pool = filterFn ? accounts.filter(filterFn) : accounts;
    const q    = search.trim().toLowerCase();
    const hits = q
      ? pool.filter(a =>
          a.name.toLowerCase().includes(q) ||
          (a.accountCode || '').toLowerCase().includes(q)
        )
      : pool;

    const result = {};
    for (const k of GROUP_ORDER) {
      const items = hits.filter(a => a.group?.nature === k);
      if (items.length) result[k] = items;
    }
    return result;
  }, [accounts, filterFn, search]);

  const selected = useMemo(
    () => (value ? accounts.find(a => String(a.id) === String(value)) || null : null),
    [value, accounts],
  );

  const hasResults = GROUP_ORDER.some(k => grouped[k]);

  return (
    <div ref={ref} className={styles.select}>
      <button type="button" className={styles.trigger} onClick={() => setOpen(o => !o)}>
        <span className={`${styles.triggerText} ${!selected ? styles.triggerPlaceholder : ''}`}>
          {selected ? selected.name : placeholder}
        </span>
        <FiChevronDown className={styles.triggerIcon} size={15} />
      </button>

      {open && (
        <div className={styles.panel}>
          {/* Search */}
          <div className={styles.searchRow}>
            <FiSearch size={13} className={styles.searchIcon} />
            <input
              ref={searchRef}
              className={styles.searchInput}
              placeholder="Search ledger…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>

          {!hasResults && <div className={styles.empty}>No accounts found</div>}

          {GROUP_ORDER.filter(k => grouped[k]).map(k => (
            <div key={k}>
              <div className={styles.groupHeader}>{GROUP_LABELS[k]}</div>
              {grouped[k].map(a => {
                const bf    = balanceMap ? fmtBal(balanceMap[String(a.id)]) : null;
                const isSel = String(a.id) === String(value);
                return (
                  <div
                    key={a.id}
                    className={`${styles.option} ${isSel ? styles.optionSelected : ''}`}
                    onClick={() => { onChange(String(a.id)); setOpen(false); }}
                  >
                    <span className={styles.optionName}>{a.name}</span>
                    {bf && (
                      <span className={`${styles.optionBal} ${bf.side === 'Dr' ? styles.optionBalDr : styles.optionBalCr}`}>
                        {bf.side} {bf.amount}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
